import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { StackedSidebarService } from '../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../services/notifications.service';
import { BaseService } from '../../../../services/base.service';
import { ChannelService, initItemsParams } from '../../../../services/channel.service';
import { ModalService } from '../../../../services/modal.service';
import { AppTableSource, ColumnSortType } from '../../../../model/table.model';
import { tableHeaders } from './my-adverts.interface';
import { IParams } from '../../../../model/http';
import { OlxAdvertService } from '../../../../services/olx-advert.service';
import { SelectionModel } from '@angular/cdk/collections';
import { parseCurrencyXXX } from '../../../../helpers';
import { DialogService } from '../../../../services/dialog.service';
import { IError } from '../../../../interfaces';
import {
  CHANNEL_STATUS,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  MODAL_TYPES,
} from '../../../../enum/constants';
import { ActivatedRoute } from '@angular/router';
import { ChangeOlxPriceComponent } from '../../../../modals/change-olx-price/change-olx-price.component';
import { PlannerComponent } from '../../../../modals/planner/planner.component';
import moment from 'moment';

@Component({
  selector: 'app-my-adverts',
  templateUrl: './my-adverts.component.html',
  styleUrls: ['./my-adverts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MyAdvertsComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();
  dataSource$: Observable<AppTableSource> = this.olxAdvertService.items$.pipe(
    map((data) => {
      tableHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });
      this.statistic = data.statistic || {};
      return {
        headers: tableHeaders,
        rows: data.items?.map((v: any) => {
          return {
            ...v,
            full_location: [v.city?.name, v.district?.name].filter(Boolean).join(', '),
            price_value: v.price?.trade
              ? activeTranslations.trade
              : parseCurrencyXXX(v.price?.value || 0, v.price?.currency),
            planned_count: 0,
            contact_name: v.contact?.name,
            contact_phone: v.contact?.phone,
            type: v.advertiser_type,
          };
        }),
        pageSize: data?.size || 0,
        pageIndex: data.page ? data.page - 1 : 0,
        length: data.total || 0,
      };
    }),
  );
  visibleColumns = tableHeaders.map((v) => v.value);
  selectionTable = new SelectionModel<any>(true, []);

  public bulkActionList: any[] = [
    // { label: 'to_plan', value: 'PLANNER' },
    { label: 'activate_now', value: 'ACTIVE_NOW' },
    { label: 'deactivate_now', value: 'DEACTIVATE_NOW' },
    { label: 'move_up_list', value: 'PUSH_UP' },
    // { label: 'ads', value: 'PAY_ADS'}
  ];

  private itemsRequest: any;
  private dataSort: any;
  public isLoader: boolean = false;

  public items: any[] = [];
  public currentChannelId: any = null;
  public statistic: any = {};
  public balance: any;

  private channelQuery = this.activatedRoute.snapshot.queryParams['channel'];

  public filterStatuses = [
    { name: 'olx.adverts.active', value: ['active'], key: 'active', color: 'green', active: true, selected: false },
    {
      name: 'olx.adverts.pending',
      value: ['new', 'disabled', 'unconfirmed', 'blocked'],
      key: 'waiting',
      color: 'gray',
      active: true,
      selected: false,
    },
    {
      name: 'olx.adverts.unpaid',
      value: ['limited', 'unpaid'],
      key: 'payment',
      color: 'orangered',
      active: true,
      selected: false,
    },
    {
      name: 'olx.adverts.inactive',
      value: ['outdated', 'removed_by_user'],
      key: 'deactivate',
      color: 'orange',
      active: true,
      selected: false,
    },
    {
      name: 'olx.adverts.rejected',
      value: ['moderated', 'removed_by_moderator'],
      key: 'canceled',
      color: 'red',
      active: true,
      selected: false,
    },
  ];

  constructor(
    private readonly stackedSidebarService: StackedSidebarService,
    private readonly notificationService: NotificationService,
    public readonly baseService: BaseService,
    private readonly olxAdvertService: OlxAdvertService,
    private readonly modalService: ModalService,
    private readonly dialogService: DialogService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly channelService: ChannelService,
  ) {}

  getItemsWithUpdatedParams(params: IParams, isLoader = false) {
    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }
    this.isLoader = isLoader;
    this.selectionTable.clear();
    this.itemsRequest = this.olxAdvertService
      .setQueryItemsParams(this.currentChannelId, params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res) => {
          this.isLoader = false;
          // this.cdr.detectChanges();

          // this.loadPlanners(res.items.map((v: any) => v.id));
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams(
              {
                page: res.page - 1,
                take: res.take,
              },
              true,
            );
          }
        },
        error: () => {
          this.isLoader = false;
          // this.cdr.detectChanges();
        },
      });
  }

  ngOnInit() {
    this.isLoader = true;
    if (this.baseService.currentCompanyInfo && this.baseService.currentCompanyInfo.settings?.is_olx_ads) {
      this.bulkActionList.push({ label: 'ads', value: 'PAY_ADS' });
    }

    this.channelService
      .getChannels({ scope: 'apps', types: [CHANNEL_TYPE.OLX], status: CHANNEL_STATUS.CONNECTED })
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (channels: any[]) => {
          this.items = channels;
          const data = {
            id: this.channelQuery || channels[0]?.id || null,
          };
          this.onValueChange(data);
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.olxAdvertService.destroy();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onSortChange(sortState: any) {
    this.dataSort = sortState;
    this.getItemsWithUpdatedParams({
      sort: sortState?.active,
      order: sortState.direction,
    });
  }

  onPaginationChange(paginationDetails: any) {
    const { pageIndex, pageSize } = paginationDetails;
    const page = pageIndex + 1;
    const take = pageSize;
    this.getItemsWithUpdatedParams({ page, take }, true);
  }

  onActionChange(event: any) {
    if (event.label === 'ACTIVE_NOW') {
      if (['active', 'new'].includes(event.status)) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.not_possible_for_active_ads,
        );
      } else {
        this.olxAdvertService
          .updateStatus(this.currentChannelId, event.id, 'activate')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.showWarning(
                '',
                activeTranslations.notifications_texts.warn.submitted_for_activation_1,
              );
              this.getItemsWithUpdatedParams({});
            },
          });
      }
    }

    if (event.label === 'DEACTIVATE_NOW') {
      if (!['active', 'limited'].includes(event.status)) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.possible_for_active_ads,
        );
      } else {
        this.olxAdvertService
          .updateStatus(this.currentChannelId, event.id, 'deactivate')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.showWarning(
                '',
                activeTranslations.notifications_texts.warn
                  .submitted_for_deactivation_1,
              );
              this.getItemsWithUpdatedParams({});
            },
          });
      }
    }

    if (event.label === 'PLANNER') {
      this.modalService
        .openModal({
          title: 'announcement_scheduler',
          translate: true,
          component: PlannerComponent,
          data: {
            modalType: MODAL_TYPES.ADVERT,
            advert_id: event.id,
            advert: event,
            channel_id: this.currentChannelId,
          },
        })
        .subscribe((res) => {
          if (res) {
            const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
            const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
            const payload = {
              name: res.name,
              type: res.x_data.type,
              status: res.x_data.status,
              planned_at: dateTime,
              x_data: {
                advert: event,
                channel_id: this.currentChannelId,
              },
            };
            this.olxAdvertService.createPlanner(payload).subscribe({
              next: () => {
                this.getItemsWithUpdatedParams({});
                this.notificationService.showSuccess(
                  '',
                  activeTranslations.notifications_texts.success.successfully_planned,
                );
              },
              error: () => {
                this.notificationService.showError(
                  '',
                  activeTranslations.notifications_texts.error.error_while_planner,
                );
              },
            });
          }
        });
    }

    if (event.label === 'CHANGE_PRICE') {
      const modalData = {
        title: 'change_price',
        translate: true,
        component: ChangeOlxPriceComponent,
        data: {
          price: event.price,
        },
      };
      this.modalService.openModal(modalData).subscribe({
        next: (res) => {
          if (!res) return;
          this.olxAdvertService
            .updatePrice(this.currentChannelId, `${event.id}`, res)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.getItemsWithUpdatedParams({});
                this.notificationService.showSuccess(
                  '',
                  activeTranslations.notifications_texts.success.successfully_updated,
                );
              },
              error: (res) => {
                (res.error?.errors || []).forEach((err: IError) => {
                  this.notificationService.showError(
                    activeTranslations.notifications_texts.error.error,
                    ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
                  );
                });
              },
            });
        },
        error: (err) => {
          console.log(err);
        },
      });
    }

    if (event.label === 'DUPLICATION_ADVERT') {
      this.olxAdvertService
        .duplicationAdvert(this.currentChannelId, `${event.id}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.getItemsWithUpdatedParams({});
          },
        });
    }

    if (event.label === 'DELETE_ADVERT') {
      if (['active'].includes(event.status)) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.not_possible_for_active_ads,
        );
      } else {
        const dialogRef = this.dialogService.openConfirm({
          text: 'dialogs.delete_advert',
        });
        dialogRef.subscribe({
          next: (res) => {
            if (!res) return;
            this.olxAdvertService
              .deleteAdvert(this.currentChannelId, `${event.id}`)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.getItemsWithUpdatedParams({});
                },
              });
          },
        });
      }
    }

    if (event.label === 'PUSH_UP') {
      if (!['active'].includes(event.status)) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.possible_for_active_ads,
        );
      } else {
        const dialogRef = this.dialogService.openConfirm({
          text: 'dialogs.push_up_advert',
        });
        dialogRef.subscribe((result) => {
          if (result) {
            this.olxAdvertService
              .pushUpAdvert(this.currentChannelId, `${event.id}`)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.getItemsWithUpdatedParams({});
                  this.initBalance();
                  const message =
                    activeTranslations.notifications_texts.success.push_up_ad +
                    event.id;
                  this.notificationService.showSuccess(
                    activeTranslations.notifications_texts.success.success,
                    message,
                  );
                },
                error: (e) => {
                  (e.error?.errors || []).forEach((err: IError) => {
                    this.notificationService.showError(
                      activeTranslations.notifications_texts.error.error,
                      ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR],
                    );
                  });
                },
              });
          }
        });
      }
    }
  }

  timerSearch: any;

  onSearchChange(search: string) {
    if (this.timerSearch) clearTimeout(this.timerSearch);
    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams(
        {
          ...initItemsParams,
          search,
        },
        true,
      );
    }, 500);
  }

  onValueChange(event: any) {
    this.currentChannelId = event?.id;
    this.baseService.setQuery('channel', this.currentChannelId);
    this.initBalance();
    this.getItemsWithUpdatedParams({}, true);
  }

  bulkActionPopup(event: any) {
    const ids: string[] = this.selectionTable.selected.map((v) => v.id).filter(Boolean);

    const allIds: string[] = this.selectionTable.selected.map((v) => v.id);

    if (event.value === 'ACTIVE_NOW') {
      this.olxAdvertService
        .updateBulkStatus(this.currentChannelId, ids, 'activate')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showWarning(
              '',
              activeTranslations.notifications_texts.warn.submitted_for_activation_2,
            );
            this.getItemsWithUpdatedParams({});
          },
        });
    }

    if (event.value === 'DEACTIVATE_NOW') {
      this.olxAdvertService
        .updateBulkStatus(this.currentChannelId, ids, 'deactivate')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showWarning(
              '',
              activeTranslations.notifications_texts.warn.submitted_for_deactivation_2,
            );
            this.getItemsWithUpdatedParams({});
          },
        });
    }

    if (event.value === 'PLANNER') {
      if (ids.length) {
        if (ids.length >= 2) {
          this.notificationService.showWarning(
            activeTranslations.notifications_texts.warn.warn,
            activeTranslations.notifications_texts.error.mass_activation_of_ads,
          );
        }
        this.modalService
          .openModal({
            title: 'ads_activation_time',
            translate: true,
            component: PlannerComponent,
            data: {
              modalType: MODAL_TYPES.ADVERT,
              adverts: ids,
              channel_id: this.currentChannelId,
            },
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            if (res) {
              const dateToString = moment(res.x_data.date).format('DD/MM/YYYY');
              const dateTime = moment(`${dateToString} ${res.x_data.time}`, 'DD/MM/YYYY HH:mm');
              const payload = {
                adverts: this.selectionTable.selected,
                name: res.name,
                status: res.x_data.status,
                type: res.x_data.type,
                planned_at: dateTime,
                channel_id: this.currentChannelId,
                step: res.x_data.step,
              };
              this.olxAdvertService.bulkPlanners(payload).subscribe({
                next: () => {
                  this.getItemsWithUpdatedParams({});
                  this.notificationService.showSuccess(
                    '',
                    activeTranslations.notifications_texts.success
                      .successfully_planned,
                  );
                },
                error: () => {
                  this.notificationService.showError(
                    '',
                    activeTranslations.notifications_texts.error.error_while_planner,
                  );
                },
              });
            }
          });
      }
    }

    if (event.value === 'PAY_ADS') {
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.CreateAds,
            maxWidth: '90vw',
          },
          {
            selectionTable: this.selectionTable.selected,
            channel_id: this.currentChannelId,
          },
        )
        .subscribe({
          next: (res) => {
            if (res === 'close') {
              this.getItemsWithUpdatedParams({});
            }
          },
        });
    }

    if (event.value === 'PUSH_UP') {
      const allStatus = this.selectionTable.selected.map((v) => v.status);
      if (allStatus.some((status) => status !== 'active')) {
        this.notificationService.showWarning(
          '',
          activeTranslations.notifications_texts.warn.possible_for_active_ads,
        );
      } else {
        const dialogRef = this.dialogService.openConfirm({
          text: 'dialogs.push_up_advert',
        });
        dialogRef.subscribe((result) => {
          if (result) {
            this.olxAdvertService
              .bulkPushUpAdvert(this.currentChannelId, allIds)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.getItemsWithUpdatedParams({});
                  this.initBalance();
                  this.notificationService.showSuccess(
                    activeTranslations.notifications_texts.success.success,
                    activeTranslations.notifications_texts.success.bulk_push_up_ad,
                  );
                },
                error: (e) => {
                  (e.error?.errors || []).forEach((err: IError) => {
                    this.notificationService.showError(
                      activeTranslations.notifications_texts.error.error,
                      ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR],
                    );
                  });
                },
              });
          }
        });
      }
    }
  }

  onFilterChange(event: string, item: any) {
    if (item.selected) {
      this.filterStatuses.forEach((v) => (v.active = true));
      this.filterStatuses.forEach((v) => (v.selected = false));
      this.getItemsWithUpdatedParams(
        {
          filters: { status: null },
        },
        true,
      );
    } else {
      this.filterStatuses.forEach((v) => (v.active = false));
      this.filterStatuses.forEach((v) => (v.selected = false));
      item.active = true;
      item.selected = true;
      this.getItemsWithUpdatedParams(
        {
          filters: { status: item?.value },
        },
        true,
      );
    }
  }

  public initBalance() {
    this.olxAdvertService
      .getBalance(this.currentChannelId)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (balance) => {
          this.balance = balance;
        },
        error: () => {},
      });
  }

  public changeAdvert(advert: any) {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.ChangeAdvert,
          maxWidth: '50vw',
        },
        {
          advert,
          channel_id: this.currentChannelId,
          type_action: 'UPDATE',
        },
      )
      .subscribe({
        next: (res) => {
          if (res === 'close') {
            this.getItemsWithUpdatedParams({});
          }
        },
      });
  }

  public buyPackage() {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BuyOlxPackageLegacy,
          maxWidth: '90vw',
        },
        { channel_id: this.currentChannelId },
      )
      .subscribe({
        next: (res) => {
          if (res === 'close') {
            this.getItemsWithUpdatedParams({});
          }
        },
      });
  }

  createAdvert() {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.ChangeAdvert,
          maxWidth: '50vw',
        },
        {
          type_action: 'CREATE',
          channel_id: this.currentChannelId,
        },
      )
      .subscribe({
        next: (res) => {
          if (res === 'close') {
            this.getItemsWithUpdatedParams({});
          }
        },
      });
  }
}
